import React, { useState, useEffect, useCallback } from "react";
import { MaterialReactTable } from "material-react-table";
import { useMediaQuery, Stack, Typography, Checkbox } from "@mui/material";
import FloatingStatusBar from "app/components/common/floatingButton/FloatingStatusBar";
import CommonTableCardView from "./CommonTableCardView";

const CommonTable1 = ({
  columns = [],
  data = [],
  maxHeight = "65vh",
  maxWidth = "100%",
  enableColumnActions = true,
  enableSorting = true,
  mobileView = true,
  // MultiSelect props (simplified)
  multiSelect = false,
  floatingActions = [], // Actions for FloatingStatusBar
  itemLabel = "item", // Label for FloatingStatusBar (e.g., "Student", "Record")
  disableSelectionIf = null, // Function to determine if a row should be disabled for selection
  onDataChange = null, // Optional callback when selection changes
  ...props
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");

  // Internal state for multiSelect
  const [tableData, setTableData] = useState(data);
  const [selectAll, setSelectAll] = useState(false);
  const dataLengthRef = React.useRef(data.length);

  // Sync tableData with external data changes (only when data length changes or initial load)
  useEffect(() => {
    // Only sync if data length changed (new data loaded) or significant change
    if (data.length !== dataLengthRef.current) {
      setTableData(data);
      setSelectAll(false);
      dataLengthRef.current = data.length;
    }
  }, [data]);

  // Sync select all state with table data
  useEffect(() => {
    if (!multiSelect) return;

    const updatedData = tableData.map((item) => {
      if (!selectAll) {
        return { ...item, checked: false };
      } else {
        // Skip disabled items
        if (disableSelectionIf && disableSelectionIf(item)) {
          return item;
        }
        return { ...item, checked: true };
      }
    });

    setTableData(updatedData);
    if (onDataChange) onDataChange(updatedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  // Handle individual row selection
  const handleRowSelection = useCallback(
    (rowId, checked) => {
      setTableData((prevData) => {
        const updatedData = [...prevData];
        updatedData[Number(rowId)].checked = checked;
        if (onDataChange) onDataChange(updatedData);
        return updatedData;
      });
    },
    [onDataChange]
  );

  // Handle select all
  const handleSelectAll = useCallback((checked) => {
    setSelectAll(checked);
  }, []);

  // Handle clear selection
  const handleClearSelection = () => {
    const uncheckedData = tableData.map((item) => ({
      ...item,
      checked: false,
    }));
    setTableData(uncheckedData);
    setSelectAll(false);
    if (onDataChange) onDataChange(uncheckedData);
  };

  // Get selected items
  const getSelectedItems = () => {
    return tableData.filter((item) => item?.checked) || [];
  };

  // Get selected count
  const getSelectedCount = () => {
    return getSelectedItems().length;
  };

  // Enhanced columns with checkbox if multiSelect is enabled
  const enhancedColumns = React.useMemo(() => {
    if (!multiSelect || columns.length === 0) return columns;

    // Clone the first column and enhance it with checkbox
    const firstColumn = { ...columns[0] };
    const originalHeader = firstColumn.Header || firstColumn.header;
    const originalCell = firstColumn.Cell;

    // Enhanced Header with "Select All" checkbox
    firstColumn.Header = ({ column }) => {
      const headerContent =
        typeof originalHeader === "function"
          ? originalHeader({ column })
          : originalHeader;

      return (
        <Stack direction="row" alignItems="center" gap={0.8}>
          {typeof headerContent === "string" ? (
            <Typography>{headerContent}</Typography>
          ) : (
            headerContent
          )}
          <Checkbox
            checked={selectAll}
            onChange={(e) => handleSelectAll(e.target.checked)}
            size="small"
            sx={{
              padding: 0,
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          />
        </Stack>
      );
    };

    // Enhanced Cell with row checkbox
    firstColumn.Cell = ({ cell, row }) => {
      const isDisabled = disableSelectionIf
        ? disableSelectionIf(row.original)
        : false;
      const isChecked = row?.original?.checked || false;

      return (
        <Stack direction="row" alignItems="center" gap={1}>
          {originalCell ? (
            originalCell({ cell, row })
          ) : (
            <Typography>
              {parseInt(cell.row.id) + 1 < 10
                ? `0${parseInt(cell.row.id) + 1}`
                : parseInt(cell.row.id) + 1}
            </Typography>
          )}
          {!isDisabled && (
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                handleRowSelection(row.id, e.target.checked);
              }}
              size="small"
              sx={{
                padding: 0,
                "& .MuiSvgIcon-root": { fontSize: 20 },
              }}
            />
          )}
        </Stack>
      );
    };

    return [firstColumn, ...columns.slice(1)];
  }, [
    columns,
    multiSelect,
    selectAll,
    disableSelectionIf,
    handleSelectAll,
    handleRowSelection,
  ]);

  // Render card view on mobile, table on desktop
  if (isMobile && mobileView) {
    return (
      <>
        <CommonTableCardView
          columns={columns}
          data={multiSelect ? tableData : data}
          // maxHeight={maxHeight}
          multiSelect={multiSelect}
          selectAll={selectAll}
          handleSelectAll={handleSelectAll}
          handleRowSelection={handleRowSelection}
          disableSelectionIf={disableSelectionIf}
        />

        {/* FloatingStatusBar - auto-rendered when multiSelect is enabled */}
        {multiSelect && (
          <FloatingStatusBar
            selectedCount={getSelectedCount()}
            onClear={handleClearSelection}
            itemLabel={itemLabel}
            show={getSelectedCount() > 0}
            actions={floatingActions}
            variant="light"
          />
        )}
      </>
    );
  }

  return (
    <>
      <MaterialReactTable
        columns={multiSelect ? enhancedColumns : columns}
        data={multiSelect ? tableData : data}
        enableStickyHeader={true}
        enablePagination={false}
        enableTopToolbar={true}
        enableBottomToolbar={false}
        enableColumnActions={enableColumnActions}
        enableSorting={enableSorting}
        muiTableProps={{
          sx: {
            tableLayout: isMobile ? "auto" : "fixed",
          },
        }}
        muiTablePaperProps={{
          sx: {
            maxWidth: isMobile || isTablet ? "100%" : maxWidth,
            boxShadow: 0,
            border: "1px solid #B6C8D6",
            borderRadius: "10px",
            marginBottom: 2,
          },
        }}
        muiTopToolbarProps={{
          sx: {
            borderRadius: "10px 10px 0 0",
            backgroundColor: "#f6f8fc",
          },
        }}
        muiTableContainerProps={{
          sx: {
            maxHeight: maxHeight,
            overflowY: "auto",
            borderRadius: "0 0 10px 10px",
            "&::-webkit-scrollbar": {
              width: 10,
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-track": {
              border: "1px solid #48657c",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#465f72",
              borderRadius: "10px",
            },
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: isMobile ? ".9rem" : "12px",
            color: "#0C2F49",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: "normal",
            fontSize: isMobile ? "1rem" : "14px",
            color: "#0C2F49",
            textAlign: "center",
            marginX: "auto",
            marginY: "auto",
          },
        }}
        muiTableHeadRowProps={{
          sx: {
            height: "50px",
            width: "100%",
            backgroundColor: "#F6F8FC",
          },
        }}
        enableColumnFilters
        muiTableBodyProps={{
          sx: {
            fontSize: "14px",
          },
        }}
        {...props}
      />

      {/* FloatingStatusBar - auto-rendered when multiSelect is enabled */}
      {multiSelect && (
        <FloatingStatusBar
          selectedCount={getSelectedCount()}
          onClear={handleClearSelection}
          itemLabel={itemLabel}
          show={getSelectedCount() > 0}
          actions={floatingActions}
          variant="light"
        />
      )}
    </>
  );
};

export default CommonTable1;
